import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { AuthChambers, AuthUser } from './types';

interface AuthState {
  accessToken: string | null;
  user: AuthUser | null;
  chambers: AuthChambers | null;
  permissions: string[];
  isHydrated: boolean;
  setSession: (data: {
    accessToken: string;
    user: AuthUser;
    chambers: AuthChambers;
    permissions: string[];
  }) => void;
  patchUser: (partial: Partial<AuthUser>) => void;
  setAccessToken: (token: string) => void;
  clear: () => void;
  setHydrated: () => void;
}

/**
 * Auth store.
 *
 * The access token is held in memory (not persisted) for security.
 * User/chambers/permissions are persisted to localStorage so the app shell
 * can render immediately on reload — but the access token is re-acquired
 * via /auth/refresh on first network call.
 */
export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      accessToken: null,
      user: null,
      chambers: null,
      permissions: [],
      isHydrated: false,
      setSession: ({ accessToken, user, chambers, permissions }) =>
        set({ accessToken, user, chambers, permissions }),
      patchUser: (partial) =>
        set(state => ({ user: state.user ? { ...state.user, ...partial } : null })),
      setAccessToken: (token) => set({ accessToken: token }),
      clear: () => set({ accessToken: null, user: null, chambers: null, permissions: [] }),
      setHydrated: () => set({ isHydrated: true }),
    }),
    {
      name: 'jurivoxa-auth',
      partialize: (state) => ({
        user: state.user,
        chambers: state.chambers,
        permissions: state.permissions,
      }),
      onRehydrateStorage: () => (state) => {
        state?.setHydrated();
      },
    },
  ),
);

export function isLoggedIn(): boolean {
  const { user } = useAuthStore.getState();
  return !!user;
}
